import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { useScheduleSync } from '@/modules/healthblocks/hooks/useScheduleSync'
import { copyToClipboard } from '@/utils/clipboard'
import { openUrl } from '@/utils/external-resources'

import type { InheritableModalProps } from '@/@bothrs-ui/web/components/modal/Modal.props'

type AgendaSyncModalProps = InheritableModalProps & {
  onAction?: () => void
}

const AgendaSyncModal = ({ ...props }: AgendaSyncModalProps) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const theme = useTheme()
  const { execute: getScheduleSyncUrl, isLoading, value: scheduleSyncUrl } = useScheduleSync()

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (props.isVisible && !scheduleSyncUrl && !isLoading) {
      getScheduleSyncUrl()
    }
  }, [props.isVisible, scheduleSyncUrl, isLoading])

  useEffect(() => {
    if (!props.isVisible) {
      setIsCopied(false)
    }
  }, [props.isVisible])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function onAndroidAction() {
    mixpanel.track('calendar_sync_android_copy')

    copyToClipboard(scheduleSyncUrl ?? '').then((copied) => {
      if (copied) {
        setIsCopied(true)
      }
    })
  }

  function onAction() {
    mixpanel.track('calendar_sync_ios_open')

    if (scheduleSyncUrl) {
      openUrl(scheduleSyncUrl)

      if (props.onClose) {
        props.onClose()
      }
    }
  }

  return (
    <Modal {...props}>
      <FlexColumn alignContent='center'>
        {isCopied ? (
          <>
            <FontAwesomeIcon icon={['fal', 'calendar']} fontSize={24} />

            <Margin mt={theme.UI.Spacing.Space5} />

            <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
              {t({
                id: 'timeline.sync.copied',
                message: 'Link gekopieerd!',
              })}
            </H3>

            <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
              {t({
                id: 'timeline.sync.paste',
                message: 'Je kan de link nu plakken in je agenda naar keuze.',
              })}
            </P>

            <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
              {t({
                id: 'timeline.sync.recopy',
                message:
                  'Je kan de link hieronder steeds opnieuw kopiëren. Als alles gelukt is, kan je dit bericht gewoon sluiten.',
              })}
            </P>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={['fal', 'calendar']} fontSize={24} />

            <Margin mt={theme.UI.Spacing.Space5} />

            <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
              {t({
                id: 'agenda.sync.title',
                message: 'Je afspraken aan je persoonlijke agenda toevoegen?',
              })}
            </H3>

            <P type='BodyMediumRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
              {Capacitor.getPlatform() === 'android'
                ? t({
                    id: 'agenda.sync.description.android',
                    message:
                      'Synchroniseer deze agenda met je agenda door de link te kopiëren en de link handmatig toe te voegen aan je eigen agenda.',
                  })
                : t({
                    id: 'timeline.sync.description.non-android',
                    message:
                      "Dat kan. Klik op de onderstaande knop 'Synchroniseren' om je afspraken automatisch op te nemen in je agenda. Je kan dit ook later instellen via de accountinstellingen in je MijnZiekenhuis profiel.",
                  })}
            </P>

            <P type='BodyMediumRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
              {t({
                id: 'agenda.sync.appointments.profile',
                message: 'Je kan dit steeds later instellen via de accountinstellingen in je profiel.',
              })}
            </P>
          </>
        )}

        {Capacitor.getPlatform() === 'android' ? (
          <Button
            variant='Primary'
            onClick={() => {
              mixpanel.track('calendar_sync_android')
              onAndroidAction()
            }}
            isLoading={isLoading}
            disabled={!isLoading && !scheduleSyncUrl}
          >
            {isCopied
              ? t({
                  id: 'agenda.sync.copy.again',
                  message: 'Link opnieuw kopiëren',
                })
              : t({
                  id: 'agenda.sync.copy',
                  message: 'Link kopiëren',
                })}
          </Button>
        ) : (
          <Button
            variant='Primary'
            onClick={() => {
              mixpanel.track('calendar_sync_ios')
              onAction()
            }}
            isLoading={isLoading}
          >
            {t({
              id: 'agenda.sync.primary',
              message: 'Synchroniseren',
            })}
          </Button>
        )}
        <Margin mt={theme.UI.Spacing.Space2} />
        <Button
          variant='Tertiary'
          onClick={() => {
            mixpanel.track('calendar_sync_cancel')
            props.onClose?.()
          }}
        >
          {t({
            id: 'agenda.sync.tertiary',
            message: 'Annuleren',
          })}
        </Button>
      </FlexColumn>
    </Modal>
  )
}

export { AgendaSyncModal }
